import {
  Group,
  MultiSelect,
  NumberInput,
  Select,
  Stack,
  Switch,
  Textarea,
  TextInput,
} from '@mantine/core';
import type { ChangeEvent } from 'react';
import { trpc } from '../lib/trpc';

/**
 * Reusable contact create / edit form. The host page owns submission
 * (so create + edit can share this body). Country + language are
 * static lists since LeadSync targets the SMB segment — adding a full
 * 240-country dropdown adds friction without payoff.
 */
export type ContactFormState = {
  firstName: string;
  lastName: string;
  waId: string;
  email: string;
  languageCode: string;
  countryId: number | null;
  whatsappOptOut: boolean;
  enableAiBot: boolean;
  enableReplyBot: boolean;
  groupIds: number[];
  customFieldValues: Record<string, string>;
};

export const blankContactForm = (): ContactFormState => ({
  firstName: '',
  lastName: '',
  waId: '',
  email: '',
  languageCode: '',
  countryId: null,
  whatsappOptOut: false,
  enableAiBot: true,
  enableReplyBot: true,
  groupIds: [],
  customFieldValues: {},
});

const COUNTRIES = [
  { value: '1', label: '🇮🇳 India' },
  { value: '2', label: '🇺🇸 United States' },
  { value: '3', label: '🇬🇧 United Kingdom' },
  { value: '4', label: '🇨🇦 Canada' },
  { value: '5', label: '🇦🇺 Australia' },
  { value: '6', label: '🇦🇪 UAE' },
  { value: '7', label: '🇸🇬 Singapore' },
  { value: '8', label: '🇩🇪 Germany' },
  { value: '9', label: '🇫🇷 France' },
  { value: '10', label: '🇧🇷 Brazil' },
];

const LANGUAGES = [
  { value: 'en', label: 'English' },
  { value: 'hi', label: 'Hindi (हिन्दी)' },
  { value: 'es', label: 'Spanish (Español)' },
  { value: 'pt', label: 'Portuguese (Português)' },
  { value: 'fr', label: 'French (Français)' },
  { value: 'de', label: 'German (Deutsch)' },
  { value: 'ar', label: 'Arabic (العربية)' },
  { value: 'zh', label: 'Chinese (中文)' },
  { value: 'ja', label: 'Japanese (日本語)' },
  { value: 'ru', label: 'Russian (Русский)' },
];

export function ContactForm({
  state,
  onChange,
}: {
  state: ContactFormState;
  onChange: (next: ContactFormState) => void;
}) {
  const groups = trpc.contacts.groups.list.useQuery();
  const customFields = trpc.contacts.customFields.list.useQuery();

  const set = <K extends keyof ContactFormState>(key: K, value: ContactFormState[K]) =>
    onChange({ ...state, [key]: value });

  return (
    <Stack gap="sm">
      <Group grow>
        <TextInput
          label="First name"
          value={state.firstName}
          onChange={(e: ChangeEvent<HTMLInputElement>) => set('firstName', e.currentTarget.value)}
        />
        <TextInput
          label="Last name"
          value={state.lastName}
          onChange={(e) => set('lastName', e.currentTarget.value)}
        />
      </Group>
      <Group grow>
        <TextInput
          label="WhatsApp number"
          placeholder="+91 99001 12233"
          value={state.waId}
          onChange={(e) => set('waId', e.currentTarget.value)}
        />
        <TextInput
          label="Email"
          type="email"
          value={state.email}
          onChange={(e) => set('email', e.currentTarget.value)}
        />
      </Group>
      <Group grow>
        <Select
          label="Country"
          data={COUNTRIES}
          searchable
          clearable
          value={state.countryId ? String(state.countryId) : null}
          onChange={(v) => set('countryId', v ? Number(v) : null)}
        />
        <Select
          label="Language"
          data={LANGUAGES}
          searchable
          clearable
          value={state.languageCode || null}
          onChange={(v) => set('languageCode', v ?? '')}
        />
      </Group>
      <MultiSelect
        label="Groups"
        data={(groups.data ?? []).map((g) => ({ value: String(g.id), label: g.title }))}
        value={state.groupIds.map(String)}
        onChange={(v) => set('groupIds', v.map(Number).filter(Number.isFinite))}
        searchable
        clearable
      />

      {(customFields.data ?? []).length > 0 && (
        <Stack gap={6} mt="xs">
          {(customFields.data ?? []).map((f) => (
            <CustomFieldInput
              key={f.id}
              field={f}
              value={state.customFieldValues[String(f.id)] ?? ''}
              onChange={(v) =>
                set('customFieldValues', {
                  ...state.customFieldValues,
                  [String(f.id)]: v,
                })
              }
            />
          ))}
        </Stack>
      )}

      <Group grow>
        <Switch
          label="Disable AI bot for this contact"
          checked={!state.enableAiBot}
          onChange={(e) => set('enableAiBot', !e.currentTarget.checked)}
        />
        <Switch
          label="Disable reply bot for this contact"
          checked={!state.enableReplyBot}
          onChange={(e) => set('enableReplyBot', !e.currentTarget.checked)}
        />
      </Group>
      <Switch
        label="Marketing opt-out (do not send campaigns)"
        checked={state.whatsappOptOut}
        onChange={(e) => set('whatsappOptOut', e.currentTarget.checked)}
      />
    </Stack>
  );
}

function CustomFieldInput(props: {
  field: { id: number; inputName: string; inputType: string; options: string | null };
  value: string;
  onChange: (v: string) => void;
}) {
  const label = props.field.inputName;
  const opts = (() => {
    if (!props.field.options) return [] as string[];
    try {
      const a = JSON.parse(props.field.options);
      return Array.isArray(a) ? a.filter((s): s is string => typeof s === 'string') : [];
    } catch {
      return [];
    }
  })();
  switch (props.field.inputType) {
    case 'textarea':
      return (
        <Textarea
          label={label}
          autosize
          minRows={2}
          value={props.value}
          onChange={(e) => props.onChange(e.currentTarget.value)}
        />
      );
    case 'number':
    case 'currency':
      return (
        <NumberInput
          label={label}
          value={props.value === '' ? '' : Number(props.value)}
          onChange={(v) => props.onChange(v === '' ? '' : String(v))}
          allowDecimal
        />
      );
    case 'select':
      return (
        <Select
          label={label}
          data={opts}
          value={props.value || null}
          onChange={(v) => props.onChange(v ?? '')}
          clearable
          searchable
        />
      );
    case 'multi-select':
      return (
        <MultiSelect
          label={label}
          data={opts}
          value={
            props.value
              ? props.value
                  .split(',')
                  .map((s) => s.trim())
                  .filter(Boolean)
              : []
          }
          onChange={(v) => props.onChange(v.join(','))}
          clearable
          searchable
        />
      );
    case 'date':
      return (
        <TextInput
          type="date"
          label={label}
          value={props.value}
          onChange={(e) => props.onChange(e.currentTarget.value)}
        />
      );
    case 'datetime-local':
      return (
        <TextInput
          type="datetime-local"
          label={label}
          value={props.value}
          onChange={(e) => props.onChange(e.currentTarget.value)}
        />
      );
    case 'time':
      return (
        <TextInput
          type="time"
          label={label}
          value={props.value}
          onChange={(e) => props.onChange(e.currentTarget.value)}
        />
      );
    case 'url':
      return (
        <TextInput
          type="url"
          label={label}
          value={props.value}
          onChange={(e) => props.onChange(e.currentTarget.value)}
        />
      );
    case 'email':
      return (
        <TextInput
          type="email"
          label={label}
          value={props.value}
          onChange={(e) => props.onChange(e.currentTarget.value)}
        />
      );
    case 'phone':
      return (
        <TextInput
          type="tel"
          label={label}
          value={props.value}
          onChange={(e) => props.onChange(e.currentTarget.value)}
        />
      );
    default:
      return (
        <TextInput
          label={label}
          value={props.value}
          onChange={(e) => props.onChange(e.currentTarget.value)}
        />
      );
  }
}
